import React, { MutableRefObject, useRef, useState } from 'react';
import classNames from 'classnames';
import { ValidationSummary } from 'utils/ValidationUtils';
import { useTrackFocus } from '../hooks/useTrackFocus';
import { ValidationWrapper } from '../Validation/ValidationWrapper';
import { IconButton } from 'components/buttons/IconButton';
import { DropDownOverlay } from '../DropDownOverlay';
// import './DataFilterField.less'
import '../text-inputs.less';
import {
  PersonalizationScopeSelector,
  IPersonalizationScopeSelectorProps,
} from 'components/Editors/data-filters/PersonalizationScopeSelector';
import { TextField } from '../TextField';

export interface IDataFilterFieldCommonProps<T> {
  validation?: ValidationSummary;
  autoFocus?: boolean;
  tabIndex?: number;
  className?: string;
  onFieldChange?: (fieldText: string) => void;
  value: T;
  /** something? */
  onChange: (value: T) => void;
  /**
   * Define to render personalization controls beside the content.
   */
  personalization?: IPersonalizationScopeSelectorProps;
  placeholder?: string;
  id?: string;
  onBlur?: React.FocusEventHandler;
}

interface IDataFilterFieldRenderProps {
  /** Just close without triggering other handlers */
  close: () => void;
  /** Trigger any close handlers and close.*/
  onClose: () => void;
  keyHandler: React.KeyboardEventHandler;
}

interface IDataFilterFieldProps<T> extends IDataFilterFieldCommonProps<T> {
  fieldText?: string;
  iconClassName?: string;
  activeIconClassName?: string;
  value: T;
  onFieldChange: (fieldText: string) => void;
  children?: (props: IDataFilterFieldRenderProps) => React.ReactNode;
}

const getClassName = (className?: string) => classNames('dp-data-filter-field', className);

const getIconClassName = (active: boolean) =>
  classNames({
    'dp-data-filter-field__icon': true,
    'dp-data-filter-field__icon--active': active,
  });

const getClasses = (isFocused?: boolean, className?: string) =>
  classNames('dp-datetime-picker', 'dp-text-input-wrapper', className, {
    'dp-text-input-wrapper--focused': isFocused,
    'dp-datetime-picker--focused': isFocused,
    // 'full-size': layout,
  });

const getInputClassNames = (asDropDown = false) => classNames({ 'dpc-wrapped-text-input': asDropDown });

/**
 * A base component for data filter fields.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function DataFilterField<T>({
  autoFocus,
  children,
  className,
  fieldText,
  id,
  onChange,
  onBlur,
  onFieldChange,
  personalization,
  placeholder,
  tabIndex,
  validation,
  value,
}: IDataFilterFieldProps<T>) {
  const inputRef: MutableRefObject<HTMLInputElement | null> = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [suggestion, setSuggestion] = useState(''); // add suggestion provider?
  const { isFocused, focusTrackingProps } = useTrackFocus({ onBlur: onBlur || acceptSuggestion });
  const wrapperRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const close = () => {
    setIsOpen(false);
  };

  // pretty sure this suggestion business is from when DateRange was date picker in here.
  function acceptSuggestion() {
    // functions are hoisted
    if (!suggestion) {
      return;
    } else {
      setSuggestion('');
      setIsOpen(false);
    }
  }

  const keyHandler = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        acceptSuggestion();
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      default:
        break;
    }
  };

  const onIconClick = () => {
    setIsOpen(!isOpen);
  };

  const onClose = () => {
    // if (suggestion && suggestion.valid === false) {
    //   return setDateTimeString(suggestion.date);
    // }
    // setSuggestion(undefined);
    setIsOpen(false);
    onChange(value);
    inputRef.current && inputRef.current.focus();
  };

  const asDropDown = false;

  const open = isOpen;
  const onFieldTextChange = (e: React.ChangeEvent<HTMLInputElement>) => onFieldChange(e.target.value);

  return (
    <>
      {!asDropDown && (
        <div className={getClassName(className)}>
          {children ? (
            children({ close, onClose, keyHandler })
          ) : (
            <TextField
              autoFocus={autoFocus}
              type="text"
              className={getInputClassNames()}
              placeholder={placeholder}
              value={fieldText || ''}
              onChange={onFieldTextChange}
              ref={inputRef}
              tabIndex={tabIndex}
              onKeyUp={keyHandler}
              id={id}
              {...focusTrackingProps}
            />
          )}
          {personalization && <PersonalizationScopeSelector {...personalization} />}
        </div>
      )}
      {/* leaving this dropdown style stuff in for now in case we hit something that doesn't play nice with the other style. */}
      {asDropDown && (
        <>
          <ValidationWrapper validation={validation}>
            <div className={getClasses(isFocused, className)} ref={wrapperRef}>
              <input
                autoComplete="off"
                autoFocus={autoFocus}
                type="text"
                className={getInputClassNames()}
                placeholder={''}
                value={fieldText}
                onChange={onFieldTextChange}
                ref={inputRef}
                tabIndex={tabIndex}
                onKeyUp={keyHandler}
                {...focusTrackingProps}
              />
              <IconButton
                className="dpc-combo-box__arrow-down"
                onClick={onIconClick}
                title={'Open'}
                iconClassName={getIconClassName(isOpen)}
                iconSize="input"
                tabIndex={-1} // we don't want this to receive tab-focus... until we have to do an accessibility overhaul.
              />
              <DropDownOverlay
                sourceRef={wrapperRef}
                isOpen={open}
                onClose={onClose}
                placement={'bottom'}
                callOutMinWidth={260}
                flipBehavior={['bottom', 'top', 'right', 'left']}
              >
                {children?.({ close, onClose, keyHandler })}
              </DropDownOverlay>
            </div>
          </ValidationWrapper>
          {personalization && <PersonalizationScopeSelector {...personalization} />}
        </>
      )}
    </>
  );
}
